export class WebPlayerSubtitleService {
    constructor(player) {
        this._listener = null;
        this._enabled = false;
        this._selectedLanguage = null;
        this._subscribedTextTrackList = null;
        this._onTrackAdded = () => this._syncFromPlayer();
        this._onTrackRemoved = () => this._syncFromPlayer();
        this._onTrackListChanged = () => this._onTracksChanged();
        this._activeCueCleanup = null;
        this._player = player;
        this._subscribeToPlayer();
        this._syncFromPlayer();
    }
    getAvailability(_stream) {
        return Promise.resolve(this._computeAvailability());
    }
    getLanguages(_stream) {
        const { languages, selected } = this._computeLanguagesAndSelected();
        return Promise.resolve({ languages, selected });
    }
    enable(_stream) {
        this._enabled = true;
        this._syncFromPlayer();
        return Promise.resolve();
    }
    disable(_stream) {
        this._enabled = false;
        this._syncFromPlayer();
        return Promise.resolve();
    }
    setLanguage(language, _stream) {
        this._selectedLanguage = language;
        this._syncFromPlayer();
        return Promise.resolve();
    }
    subscribe(listener) {
        this._listener = listener;
        const availability = this._computeAvailability();
        listener.onAvailabilityChanged?.(availability);
        if (availability === "available") {
            const { languages, selected } = this._computeLanguagesAndSelected();
            listener.onLanguagesChanged?.(languages, selected);
        }
        return () => {
            if (this._listener === listener)
                this._listener = null;
        };
    }
    dispose() {
        this._unsubscribeFromPlayer();
        this._cleanupActiveCueListener();
        this._listener = null;
    }
    _subscribeToPlayer() {
        this._unsubscribeFromPlayer();
        const list = this._player.textTracks;
        list.addEventListener("addtrack", this._onTrackAdded);
        list.addEventListener("removetrack", this._onTrackRemoved);
        list.addEventListener("change", this._onTrackListChanged);
        this._subscribedTextTrackList = list;
    }
    _unsubscribeFromPlayer() {
        const list = this._subscribedTextTrackList;
        if (!list)
            return;
        list.removeEventListener("addtrack", this._onTrackAdded);
        list.removeEventListener("removetrack", this._onTrackRemoved);
        list.removeEventListener("change", this._onTrackListChanged);
        this._subscribedTextTrackList = null;
    }
    _computeAvailability() {
        const list = this._player.textTracks;
        return list.length > 0 ? "available" : "none";
    }
    _getTrackAt(i) {
        const list = this._player.textTracks;
        return list[i] ?? null;
    }
    _forEachTextTrack(cb) {
        const list = this._player.textTracks;
        for (let i = 0; i < list.length; i++) {
            const t = list[i];
            if (t)
                cb(t);
        }
    }
    _computeLanguagesAndSelected() {
        const raw = [];
        this._forEachTextTrack(t => {
            const code = (t.language || "und").toLowerCase();
            const label = (t.label && t.label.trim().length > 0) ? t.label : code.toUpperCase();
            raw.push({ code, label });
        });
        const hasIso639_1 = raw.some(l => l.code.length === 2);
        const filtered = hasIso639_1
            ? raw.filter(l => l.code.length === 2 || l.code === "und")
            : raw;
        const seen = new Set();
        const languages = [];
        for (const l of filtered) {
            if (seen.has(l.code))
                continue;
            seen.add(l.code);
            languages.push(l);
        }
        let selected = this._selectedLanguage;
        if (selected)
            selected = selected.toLowerCase();
        const hasSelected = selected ? languages.some(l => l.code === selected) : false;
        if (!hasSelected)
            selected = languages[0]?.code ?? null;
        return { languages, selected };
    }
    _applySelection(selected) {
        if (!selected) {
            this._forEachTextTrack(t => (t.mode = "disabled"));
            return;
        }
        const desired = selected.toLowerCase();
        let selectedTrack = null;
        this._forEachTextTrack(t => {
            if (!selectedTrack && (t.language || "").toLowerCase() === desired)
                selectedTrack = t;
        });
        if (!selectedTrack)
            selectedTrack = this._getTrackAt(0);
        if (!selectedTrack)
            return;
        this._forEachTextTrack(t => {
            t.mode = (this._enabled && t === selectedTrack) ? "showing" : "disabled";
        });
    }
    _getActiveTrack() {
        let showing = null;
        this._forEachTextTrack(t => {
            if (!showing && t.mode === "showing")
                showing = t;
        });
        return showing ?? this._getTrackAt(0);
    }
    _cleanupActiveCueListener() {
        if (!this._activeCueCleanup)
            return;
        this._activeCueCleanup();
        this._activeCueCleanup = null;
    }
    _syncActiveCueListener() {
        this._cleanupActiveCueListener();
        const track = this._getActiveTrack();
        if (!track)
            return;
        const onCueChange = () => this._emitTextFromActive();
        track.addEventListener("cuechange", onCueChange);
        this._activeCueCleanup = () => track.removeEventListener("cuechange", onCueChange);
    }
    _syncFromPlayer() {
        const availability = this._computeAvailability();
        const l = this._listener;
        if (availability !== "available") {
            this._cleanupActiveCueListener();
            l?.onAvailabilityChanged?.(availability);
            l?.onLanguagesChanged?.([], this._selectedLanguage);
            return;
        }
        const { languages, selected } = this._computeLanguagesAndSelected();
        this._selectedLanguage = selected;
        this._applySelection(selected);
        this._syncActiveCueListener();
        l?.onAvailabilityChanged?.(availability);
        l?.onLanguagesChanged?.(languages, selected);
        this._emitTextFromActive();
    }
    _onTracksChanged() {
        this._syncActiveCueListener();
        this._emitTextFromActive();
    }
    _emitTextFromActive() {
        if (!this._enabled)
            return;
        if (this._computeAvailability() !== "available")
            return;
        const track = this._getActiveTrack();
        if (!track)
            return;
        const text = this._extractTextFromActiveCues(track);
        // if (!text) return;
        const language = (track.language || this._selectedLanguage || "und").toLowerCase();
        const payload = { text, receivedAt: Date.now(), language };
        this._listener?.onText?.(payload);
    }
    _extractTextFromActiveCues(track) {
        const cues = track.activeCues;
        if (!cues || cues.length === 0)
            return "";
        const parts = [];
        for (let i = 0; i < cues.length; i++) {
            const cue = cues[i];
            if (!cue)
                continue;
            if (typeof VTTCue !== "undefined" && cue instanceof VTTCue) {
                const t = cue.text.trim();
                if (t)
                    parts.push(t);
                continue;
            }
            const maybeText = cue.text;
            if (typeof maybeText === "string") {
                const t = maybeText.trim();
                if (t)
                    parts.push(t);
            }
        }
        return parts.join("\n").trim();
    }
}
